/**
 * Photo Moderation Modal - Alpine.js Component
 * Opens a modal for moderators to approve/delete photos
 */

import { lockBodyScroll, unlockBodyScroll } from '../modules/scroll-lock.js';

export function photoModModal() {
    return {
        isOpen: false,
        isSubmitting: false,
        error: '',
        action: '8',
        reason: '80',
        deleteReasonLabels: {},

        get needsReason() {
            return this.action === '7';
        },

        get reasonLabels() {
            return this.deleteReasonLabels;
        },

        init() {
            const el = this.$root;
            try {
                this.deleteReasonLabels = JSON.parse(el.dataset.reasonLabels || '{}');
            } catch {
                this.deleteReasonLabels = {};
            }
            this._deletedText = el.dataset.deletedText || '';
            this._approvedText = el.dataset.approvedText || '';
            window.openPhotoModModal = () => this.open();
        },

        open() {
            this.isOpen = true;
            this.error = '';
            window._photoModModalOpen = true;
            lockBodyScroll();
            this.$nextTick(() => {
                window.LucideIcons?.initialize(this.$el);
            });
        },

        close() {
            if (!this.isOpen) return;
            this.isOpen = false;
            window._photoModModalOpen = false;
            unlockBodyScroll();
        },

        _showToast(message) {
            const toast = document.createElement('div');
            toast.className = 'fixed top-4 z-[10003] ' +
                'bg-green-600 text-white px-5 py-3 rounded-xl shadow-lg ' +
                'text-sm font-medium flex items-center gap-2';
            toast.style.cssText = 'opacity:0; transform:translateX(-50%) translateY(-8px); ' +
                'transition:opacity .3s, transform .3s;';

            // Center over the photo area (between sidebar and comments panel)
            const lightbox = document.querySelector('[x-ref="lightbox"]');
            const photoArea = lightbox?.querySelector('.flex-1');
            if (photoArea) {
                const rect = photoArea.getBoundingClientRect();
                toast.style.left = (rect.left + rect.width / 2) + 'px';
            } else {
                toast.style.left = '50%';
            }

            const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            svg.setAttribute('class', 'w-5 h-5 flex-shrink-0');
            svg.setAttribute('fill', 'none');
            svg.setAttribute('viewBox', '0 0 24 24');
            svg.setAttribute('stroke', 'currentColor');
            svg.setAttribute('stroke-width', '2');
            const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            path.setAttribute('stroke-linecap', 'round');
            path.setAttribute('stroke-linejoin', 'round');
            path.setAttribute('d', 'M5 13l4 4L19 7');
            svg.appendChild(path);

            const span = document.createElement('span');
            span.textContent = message;

            toast.appendChild(svg);
            toast.appendChild(span);
            document.body.appendChild(toast);

            requestAnimationFrame(() => {
                toast.style.opacity = '1';
                toast.style.transform = 'translateX(-50%) translateY(0)';
            });

            setTimeout(() => {
                toast.style.opacity = '0';
                toast.style.transform = 'translateX(-50%) translateY(-8px)';
                setTimeout(() => toast.remove(), 300);
            }, 3000);
        },

        _onSuccess() {
            this.close();
            const isDelete = this.action === '7';
            this._showToast(isDelete ? this._deletedText : this._approvedText);

            if (isDelete) {
                const albumUrl = this.$root.dataset.albumUrl;
                setTimeout(() => { window.location.href = albumUrl; }, 1500);
            }
        },

        async submit() {
            this.isSubmitting = true;
            this.error = '';

            try {
                const el = this.$root;
                const photoId = el.dataset.photoId;
                const userId = el.dataset.userId;

                const formData = new FormData();
                formData.append('action_set', 'photo_report');
                formData.append('pid', photoId);
                formData.append('uid', userId);
                formData.append('todo[0]', this.action);
                formData.append('log_msg', '');
                if (this.needsReason) {
                    formData.append('todo[1]', this.reason);
                }
                // The legacy /app/mod/action endpoint enforces the session CSRF
                // token. Without it the request is rejected with a redirect to
                // the landing page, which a raw fetch silently follows and dumps
                // as HTML. Send the token (mirrors the reports-queue path).
                formData.append('csrf_token', el.dataset.csrfToken || '');

                const response = await fetch('/app/mod/action?async=1', {
                    method: 'POST',
                    body: formData,
                    // Mark as XHR so an auth/CSRF gate returns a JSON redirect
                    // rather than a full HTML page.
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest',
                        Accept: 'application/json'
                    }
                });

                const failedMsg = window.t ? window.t('common.request_failed') : 'Request failed';
                if (!response.ok || response.redirected) {
                    throw new Error(failedMsg);
                }

                const result = (await response.text()).trim();

                // The endpoint returns the string '0' on success. Anything else
                // (a JSON redirect, an error code) is a failure — show a clean
                // message instead of the raw response body.
                if (result !== '0') {
                    throw new Error(failedMsg);
                }

                this.isSubmitting = false;
                this._onSuccess();

            } catch (e) {
                this.error = e.message || (window.t ? window.t('common.request_failed') : 'An error occurred');
                this.isSubmitting = false;
            }
        }
    };
}
